/**
 * TourProvider — guided-tour engine context.
 *
 * startTour('find-the-fire') looks up TOURS and drives TourOverlay through
 * the steps. Route-aware (navigates before each step), never traps the user
 * (Next/Skip always available), and target-tolerant (missing data-tour
 * elements fall back to a centered card after a short wait).
 *
 * Mounted in AppLayout so tours can reach TopBar elements (bell, View as).
 */
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { TOURS } from './tours';
import TourOverlay from './TourOverlay';
import { emitTourEvent } from '../onboarding/onboardingEvents';

const TourContext = createContext(null);

export function useTour() {
  const ctx = useContext(TourContext);
  // Graceful no-op outside the provider (e.g. public pages).
  return (
    ctx || {
      activeTourKey: null,
      startTour: () => {},
      stopTour: () => {},
    }
  );
}

export default function TourProvider({ children }) {
  const [tourKey, setTourKey] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);

  const startTour = useCallback((key) => {
    if (!TOURS[key]) return false;
    setTourKey(key);
    setStepIndex(0);
    emitTourEvent('started', key);
    return true;
  }, []);

  // Explicit exit (X, Esc, Skip) — distinct from finishing the last step.
  const stopTour = useCallback(() => {
    setTourKey((k) => {
      if (k) emitTourEvent('skipped', k, { stepIndex });
      return null;
    });
    setStepIndex(0);
  }, [stepIndex]);

  const steps = tourKey ? TOURS[tourKey] : null;

  const next = useCallback(() => {
    setStepIndex((i) => {
      if (!steps || i + 1 >= steps.length) {
        // Finished the last step.
        if (tourKey) emitTourEvent('completed', tourKey);
        setTourKey(null);
        return 0;
      }
      return i + 1;
    });
  }, [steps, tourKey]);

  const back = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  const value = useMemo(
    () => ({ activeTourKey: tourKey, startTour, stopTour }),
    [tourKey, startTour, stopTour]
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      {steps && (
        <TourOverlay
          steps={steps}
          stepIndex={stepIndex}
          onNext={next}
          onBack={back}
          onSkip={stopTour}
        />
      )}
    </TourContext.Provider>
  );
}
